import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import { RootState } from "reducers/rootReducer";
import { SocketTopics } from "constants/enums";
import { BeamSensorDevice, BeamSensorReading } from "./types";
import { latestRxSample, toSensorReadings } from "./adapters";
import {
  DEMO_SENSOR_DEVICES,
  DEMO_TICK_MS,
  demoSensorReadings,
} from "./demoData";
import BeamIcon from "./BeamIcon";
import "./MessageTypes.scss";

/**
 * beamRx message type — the live feed of a sensor that exists on this page.
 *
 * This is the beamRx dashboard reduced to the one thing a page reader wants
 * from it: what this sensor is reporting right now. Everything the full
 * dashboard adds around that — device fleet management, provisioning,
 * historical reports, threshold configuration — belongs to beamRx itself and
 * is not reproduced here, because a second, thinner copy of those screens
 * would be one more place for them to disagree.
 *
 * Readings are whatever the device publishes. Nothing is placeholdered: a
 * metric the firmware does not send simply has no tile, rather than a tile
 * reading zero.
 */

interface RxSensorCardProps {
  devices: BeamSensorDevice[];
  socket: ISocket;
}

/** How many samples the inline trend keeps. */
const TREND_LENGTH = 24;

const Sparkline: FC<{ points: number[]; color: string }> = ({
  points,
  color,
}) => {
  // One point is a reading, not a trend — a flat line drawn through it would
  // claim a history the card does not have yet.
  if (points.length < 2) return null;

  const min = Math.min(...points);
  const max = Math.max(...points);
  const span = max - min || 1;
  const path = points
    .map((value, index) => {
      const x = (index / (points.length - 1)) * 100;
      const y = 100 - ((value - min) / span) * 100;
      return `${index === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");

  return (
    <svg
      className="mt-sensor__spark"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <path d={path} fill="none" stroke={color} strokeWidth="4" />
    </svg>
  );
};

const RxSensorCard: FC<RxSensorCardProps> = ({
  devices: pageDevices,
  socket,
}) => {
  const { rx } = useSelector((state: RootState) => state.dashboard);

  /*
    Without a sensor on the page this card can only say that there is no
    sensor, which reviews nothing — the live feed it exists to show has never
    been on screen. A page with no `rx` device therefore falls back to sample
    devices, labelled as such, and drops the fallback the instant a real one
    publishes.
  */
  const isDemo = !pageDevices.length;
  const devices = isDemo ? DEMO_SENSOR_DEVICES : pageDevices;

  const [selectedId, setSelectedId] = useState<string>(devices[0]?.id || "");
  const [readings, setReadings] = useState<BeamSensorReading[]>([]);
  const [trends, setTrends] = useState<Record<string, number[]>>({});
  const [receivedAt, setReceivedAt] = useState<number>(0);

  const selected = useMemo(
    () => devices.find((device) => device.id === selectedId) || devices[0],
    [devices, selectedId]
  );

  /**
   * One place where a batch of readings lands, so a sample off the socket and
   * a sample from the demo feed build the same trend the same way.
   */
  const applyReadings = useCallback((incoming: BeamSensorReading[]) => {
    if (!incoming.length) return;
    setReadings(incoming);
    setTrends((previous) => {
      const next = { ...previous };
      incoming.forEach((reading) => {
        const value = Number(reading.value);
        if (Number.isNaN(value)) return;
        next[reading.key] = [...(next[reading.key] || []), value].slice(
          -TREND_LENGTH
        );
      });
      return next;
    });
    setReceivedAt(Date.now());
  }, []);

  /** Only the sensor being watched is subscribed to. */
  useEffect(() => {
    const topic = selected?.iotTopic;
    if (isDemo || !topic) return;
    socket.subscribeToEvent(SocketTopics.SubscribePageTopics, topic);
    return () => {
      socket.unSubscribeToEvent(SocketTopics.UnSubscribePageTopics, topic);
    };
  }, [selected?.iotTopic]);

  /** Switching devices clears the feed so one sensor's trend never continues into another's. */
  useEffect(() => {
    setReadings([]);
    setTrends({});
    setReceivedAt(0);
  }, [selected?.id]);

  /**
   * A sample device publishes nothing, so the feed is produced here on the
   * cadence a real one would use — a still card would hide the trend lines and
   * the deltas, which are most of what this view is.
   */
  useEffect(() => {
    if (!isDemo || !selected?.deviceKey) return;
    const deviceKey = selected.deviceKey;
    let tick = 0;
    const publish = () => {
      applyReadings(demoSensorReadings(deviceKey, tick));
      tick += 1;
    };
    publish();
    const handle = setInterval(publish, DEMO_TICK_MS);
    return () => clearInterval(handle);
  }, [isDemo, selected?.deviceKey, applyReadings]);

  useEffect(() => {
    if (isDemo) return;
    const sample = latestRxSample(rx);
    if (!sample) return;

    // The payload carries the device it came from; a page with two sensors
    // subscribed in quick succession can still have the other one's sample in
    // flight, and applying it here would show it under the wrong device.
    const sampleDevice = sample.deviceId || sample.deviceKey;
    if (sampleDevice && selected?.deviceKey && sampleDevice !== selected.deviceKey)
      return;

    applyReadings(toSensorReadings(sample));
  }, [rx, selected?.id]);

  return (
    <section className="mt-card mt-sensors">
      <header className="mt-card__head">
        <h4>
          {selected?.name || "Sensor"}
          {isDemo && <span className="mt-demo-badge">Demo</span>}
        </h4>
        <span className={`mt-sensors__status ${receivedAt ? "is-live" : ""}`}>
          <i />
          {receivedAt
            ? `live · ${moment(receivedAt).fromNow()}`
            : selected?.iotTopic
            ? "waiting for data"
            : "no topic"}
        </span>
      </header>

      {isDemo && (
        <p className="mt-demo-note">
          No beamRx sensor has published to this page yet — this is a sample
          feed, shown so the view can be reviewed. It is replaced the moment a
          real sensor connects.
        </p>
      )}

      {/* One sensor needs no picker; the header already names it. */}
      {devices.length > 1 && (
        <div className="mt-chip-row mt-sensors__picker">
          {devices.map((device) => (
            <button
              key={device.id}
              type="button"
              className={`mt-chip ${
                device.id === selected?.id ? "is-active" : ""
              }`}
              onClick={() => setSelectedId(device.id)}
            >
              <BeamIcon name="heart" size={12} />
              {device.name}
            </button>
          ))}
        </div>
      )}

      <div className="mt-card__body mt-sensors__grid">
        {readings.length ? (
          readings.map((reading) => {
            const points = trends[reading.key] || [];
            const previous = points.length > 1 ? points[points.length - 2] : undefined;
            const current = Number(reading.value);
            const delta =
              previous !== undefined && !Number.isNaN(current)
                ? current - previous
                : undefined;

            return (
              <article key={reading.key} className="mt-sensor">
                <span className="mt-sensor__label">{reading.label}</span>
                <div className="mt-sensor__value">
                  <strong>{reading.value}</strong>
                  {reading.unit && <span>{reading.unit}</span>}
                  {delta !== undefined && delta !== 0 && (
                    <em className={delta > 0 ? "is-up" : "is-down"}>
                      {delta > 0 ? "▲" : "▼"} {Math.abs(delta).toFixed(1)}
                    </em>
                  )}
                </div>
                <Sparkline points={points} color="#508afa" />
                {reading.timestamp ? (
                  <span className="mt-sensor__time">
                    {moment(reading.timestamp).format("h:mm:ss A")}
                  </span>
                ) : null}
              </article>
            );
          })
        ) : (
          <p className="mt-empty mt-empty--inline">
            {selected?.iotTopic
              ? "Subscribed. The first reading appears as soon as the sensor publishes."
              : "This sensor publishes no topic, so no live readings arrive for it."}
          </p>
        )}
      </div>
    </section>
  );
};

export default RxSensorCard;
